import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { components } from "../../types/api";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

type FormattedInternalCampaign =
  components["schemas"]["FormattedInternalCampaign"];

interface InternalCampaignTableProps {
  internalCampaigns: FormattedInternalCampaign[];
  maxHeight?: number;
  stickyHeader?: boolean;
}

const InternalCampaignTable: React.FC<InternalCampaignTableProps> = ({
  internalCampaigns,
  maxHeight,
  stickyHeader = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const handleNavigation = (internalCampaign: FormattedInternalCampaign) => {
    navigate(`/metrics/internal-metrics/${internalCampaign.id}`, {
      state: { internalCampaignName: internalCampaign.displayName },
    });
  };

  const handleRowClick = (internalCampaign: FormattedInternalCampaign) => {
    handleNavigation(internalCampaign);
  };

  if (!internalCampaigns || internalCampaigns.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {t(
            "metrics.internalCampaignTable.noData",
            "No internal campaigns available"
          )}
        </Typography>
      </Box>
    );
  }

  const headerCellSx = {
    fontWeight: 700,
    fontSize: "11px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
    color: theme.palette.text.secondary,
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        maxHeight: maxHeight ? `${maxHeight}px` : undefined,
        overflow: "auto",
      }}
    >
      <Table aria-label="internal campaigns table" stickyHeader={stickyHeader}>
        <TableHead
          sx={{
            "& th": {
              backgroundColor: theme.palette.background.paper,
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          <TableRow>
            <TableCell sx={headerCellSx}>
              {t("metrics.internalCampaigns", "Internal Campaigns")}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.startDate", "Start Date")}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.endDate", "End Date")}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.platform", "Platform")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {internalCampaigns.map((internalCampaign, index) => (
            <TableRow
              key={internalCampaign.id}
              onClick={() => handleRowClick(internalCampaign)}
              sx={{
                cursor: "pointer",
                backgroundColor:
                  index % 2 === 1
                    ? theme.palette.action.hover
                    : theme.palette.background.paper,
                "& td": {
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
                "&:hover": {
                  backgroundColor: theme.palette.action.selected,
                },
                "&:last-of-type td": { borderBottom: "none" },
              }}
            >
              <TableCell>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {internalCampaign.displayName}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{
                    display: "block",
                    maxWidth: 260,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {internalCampaign.id}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {internalCampaign.startDate
                    ? dayjs(internalCampaign.startDate).format("DD MMM YYYY")
                    : "-"}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {internalCampaign.endDate
                    ? dayjs(internalCampaign.endDate).format("DD MMM YYYY")
                    : "-"}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {internalCampaign.internalPlatform}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default InternalCampaignTable;
